import { useEffect, useRef, useState } from 'react';

import Box from '~/common/components/Box';

import Pages from './Pages';
import { defaultScale } from './constants';

type AreaProps = {
  pdf?: any;
  display?: any;
  scale?: any;
  highlights?: any;
  pageRotations?: any;
  onRenderSuccess?: any;
  onLoadSuccess?: any;
  onLoadError?: any;
  onScaleChange?: any;
  pageNumber?: any;
  setPageNumber?: any;
  citations?: any;
  isFullPage?: boolean;
  showHighLight?: boolean;
};

function Area(props: Readonly<AreaProps>) {
  const {
    pdf,
    display,
    scale,
    highlights,
    pageRotations,
    onRenderSuccess,
    onLoadSuccess,
    onLoadError,
    onScaleChange,
    pageNumber,
    citations,
    isFullPage,
    showHighLight,
  } = props;
  const [cursor, setCursor] = useState('default');
  const containerRef = useRef(null);
  const containerSizeRef = useRef(null);
  const pagesRef = useRef([]);
  const pageSizesRef = useRef([]);
  const pageCountRef = useRef(0);
  const scrollBarWidthRef = useRef(0);
  const hasAutoScaledFullPageRef = useRef(false);
  const hasAutoScaledFitPageRef = useRef(false);
  const lastScrolledPageNumberRef = useRef(0);
  const hasFirstPageRenderRef = useRef(false);
  const calculatedPageRotationsRef = useRef([]);
  const lastCitationRef = useRef(null);

  useEffect(() => {
    pagesRef.current = [];
    pageSizesRef.current = [];
    hasAutoScaledFullPageRef.current = false;
    hasAutoScaledFitPageRef.current = false;
    lastScrolledPageNumberRef.current = 0;
    hasFirstPageRenderRef.current = false;
  }, [pdf]);

  useEffect(() => {
    if (containerRef.current != null && containerRef.current.offsetWidth > 0) {
      containerSizeRef.current = {
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
        clientWidth: containerRef.current.clientWidth,
        clientHeight: containerRef.current.clientHeight,
      };
    }
    if (
      display !== 'none' &&
      pageNumber != 0 &&
      hasFirstPageRenderRef.current
    ) {
      scrollToPageNumber(pageNumber);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [display, pageNumber]);

  useEffect(() => {
    if (pageNumber != 0 && hasFirstPageRenderRef.current) {
      scrollToPageNumber(pageNumber);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [highlights]);

  useEffect(() => {
    if (!citations || citations.length === 0 || !hasFirstPageRenderRef.current)
      return;
    const citation = citations[0];
    if (lastCitationRef.current === citation) return;
    lastCitationRef.current = citation;
    navigateToCitation(citation);
  }, [citations]);

  function handleOnRenderSuccess(e) {
    pageSizesRef.current[e.pageNumber - 1] = {
      width: e.originalWidth,
      height: e.originalHeight,
    };

    if (pageNumber == e.pageNumber) {
      scrollToPageNumber(pageNumber);
    } else if (
      (pageNumber == 0 || pageNumber == undefined) &&
      e.pageNumber == 1 &&
      !hasFirstPageRenderRef.current
    ) {
      scrollToPageNumber(1);
    }

    hasFirstPageRenderRef.current = true;
    onRenderSuccess?.(e);
  }

  function handleOnLoadSuccess(e) {
    pageCountRef.current = e.numPages;
    if (onLoadSuccess) {
      onLoadSuccess(e);
    }
  }

  function handleSetPageRef(pageNumber, element) {
    pagesRef.current[pageNumber - 1] = element;
  }

  function handleClearPageRef() {
    pagesRef.current = [];
  }

  function getPageRotation(number) {
    let rot = 0;
    if (
      calculatedPageRotationsRef.current != null &&
      calculatedPageRotationsRef.current.length > 0
    ) {
      const page = calculatedPageRotationsRef.current.find(
        (pr) => pr.pageNumber == number,
      );
      if (page != null) {
        rot = page.rotationAngle;
      }
    }
    return rot;
  }

  function handleSetCalculatedPageRotations(newCalculatedPageRotations) {
    calculatedPageRotationsRef.current = newCalculatedPageRotations;
  }

  function navigateToCitation(citation) {
    if (!citation?.pageNumber) return;

    const pageNumber = citation.pageNumber;
    lastScrolledPageNumberRef.current = pageNumber;

    if (containerRef.current && pagesRef.current?.[pageNumber - 1]) {
      const page = pagesRef.current[pageNumber - 1];
      const centerX = (citation.x1 + citation.x3) / 2;
      const centerY = (citation.y1 + citation.y3) / 2;

      const pageTop = page.offsetTop - containerRef.current.offsetTop;
      const pageLeft = page.offsetLeft - containerRef.current.offsetLeft;

      const citationTop = page.clientHeight * centerY;
      const centralVerticalPoint = pageTop + citationTop;
      const scrollTopPosition =
        centralVerticalPoint - containerRef.current.offsetHeight / 2;

      const citationLeft = page.clientWidth * centerX;
      const centralHorizontalPoint = pageLeft + citationLeft;
      const scrollLeftPosition =
        centralHorizontalPoint - containerRef.current.offsetWidth / 2;

      containerRef.current.scrollTop = scrollTopPosition;
      containerRef.current.scrollLeft = scrollLeftPosition;
    }
  }

  function scrollToPageNumber(number) {
    if (number == 0 || number == undefined) {
      return;
    }
    lastScrolledPageNumberRef.current = number;
    try {
      if (
        containerRef.current != null &&
        pagesRef?.current[number - 1] != undefined &&
        pagesRef?.current[number - 1] != null
      ) {
        //Find the rotation of the page if it exists
        const rotationAngle = getPageRotation(number);
        //Scrolls to the desired page
        if (highlights != null && highlights.length > 0) {
          if (!hasAutoScaledFitPageRef.current) {
            autoScalePage(number, rotationAngle, isFullPage ?? true);
            hasAutoScaledFitPageRef.current = true;
          }
          const h = highlights[0];
          //Scroll vertically
          const highlightTop =
            pagesRef.current[number - 1].clientHeight * (h.top + h.height / 2);
          const pageTop =
            pagesRef.current[number - 1].offsetTop -
            containerRef.current.offsetTop;
          const centralVerticalPoint = pageTop + highlightTop;
          const scrollTopPosition =
            centralVerticalPoint - containerRef.current.offsetHeight / 2;
          containerRef.current.scrollTop = scrollTopPosition;
          //Scroll horizontally if needed
          const hasHorizontalScroll =
            containerRef.current.scrollWidth > containerRef.current.clientWidth;
          if (hasHorizontalScroll) {
            const highlightLeft =
              pagesRef.current[number - 1].clientWidth * (h.left + h.width / 2);
            const pageLeft =
              pagesRef.current[number - 1].offsetLeft -
              containerRef.current.offsetLeft;
            const centralHorizontalPoint = pageLeft + highlightLeft;
            const scrollLeftPosition =
              centralHorizontalPoint - containerRef.current.offsetWidth / 2;
            containerRef.current.scrollLeft = scrollLeftPosition;
          }
        } else {
          if (
            !hasAutoScaledFullPageRef.current &&
            !hasAutoScaledFitPageRef.current
          ) {
            autoScalePage(number, rotationAngle, isFullPage ?? true);
            hasAutoScaledFullPageRef.current = true;
          }
          //Check if we're not already scrolled to the page
          if (
            containerRef.current.scrollTop <
              pagesRef.current[number - 1].offsetTop -
                containerRef.current.offsetTop ||
            containerRef.current.scrollTop >
              pagesRef.current[number - 1].offsetTop -
                containerRef.current.offsetTop +
                pagesRef.current[number - 1].clientHeight
          ) {
            containerRef.current.scrollTop =
              pagesRef.current[number - 1].offsetTop -
              containerRef.current.offsetTop;
          }
        }
      }
    } catch {
      //TODO: Handle error
    }
  }

  function autoScalePage(number, rotationAngle, fullPage) {
    try {
      //Get the scrollbar width if needed
      if (scrollBarWidthRef.current == 0) {
        scrollBarWidthRef.current = getScrollbarWidth();
      }
      //Update scaling
      let pageWidth = pageSizesRef.current[number - 1].width;
      let pageHeight = pageSizesRef.current[number - 1].height + 2; //Add 2 to height for padding
      //Rotated pages should have swapped height/width
      if (rotationAngle == 90 || rotationAngle == 270) {
        pageWidth = pageSizesRef.current[number - 1].height;
        pageHeight = pageSizesRef.current[number - 1].width + 2; //Add 2 to height for padding
      }
      let containerWidth,
        containerHeight = 0;
      if (
        containerRef.current != null &&
        containerRef.current.offsetWidth > 0
      ) {
        containerWidth = containerRef.current.offsetWidth;
        containerHeight = containerRef.current.offsetHeight;
      }
      if (containerWidth == 0 || containerHeight == 0) {
        if (containerSizeRef.current != null) {
          containerWidth = containerSizeRef.current.width;
          containerHeight = containerSizeRef.current.height;
        }
      }
      //Calculate new scale
      let newScale = getNewScale(
        pageWidth,
        pageHeight,
        containerWidth,
        containerHeight,
        fullPage,
      );
      //Check if scroll viewer will be displayed, re-scale if necessary
      let reScale = false;
      if (pageHeight * newScale > containerHeight) {
        containerWidth -= scrollBarWidthRef.current;
        reScale = true;
      }
      if (pageWidth * newScale > containerWidth) {
        containerHeight -= scrollBarWidthRef.current;
        reScale = true;
      }
      if (reScale) {
        newScale = getNewScale(
          pageWidth,
          pageHeight,
          containerWidth,
          containerHeight,
          fullPage,
        );
      }
      //Check that scale is not less than or equal to zero
      // IF it is set to default
      if (newScale <= 0) {
        newScale = defaultScale;
      }
      //Update the scale
      if (scale != newScale) {
        onScaleChange?.(newScale);
      }
    } catch {
      //TODO: Handle Error
    }
  }

  function getNewScale(
    pageWidth,
    pageHeight,
    containerWidth,
    containerHeight,
    isFullPageScaling,
  ) {
    if (isFullPageScaling) {
      // When the aspect ratio of the page is greater than the container,
      // the width of the page will be the limiting factor.
      // So calculate the scale factor based on width
      const containerAspectRatio = containerWidth / containerHeight;
      const pageAspectRatio = pageWidth / pageHeight;
      if (pageAspectRatio > containerAspectRatio) {
        return containerWidth / pageWidth;
      }
      // Otherwise, the height of the page will be the limiting factor.
      // So calculate the scale factor based on height
      return containerHeight / pageHeight;
    } else if (pageHeight > pageWidth) {
      //Size the page to fit the width of the viewer, we can auto-scroll to the part of the page with the highlight.
      return containerWidth / pageWidth;
    } else {
      return containerHeight / pageHeight;
    }
  }

  function getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    // @ts-expect-error msOverflowStyle isn't typed correctly
    outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOM
    document.body.removeChild(outer);

    return scrollbarWidth;
  }

  function handleScroll() {
    //setPageNumber(0);
  }

  function handleMouseMove(e) {
    const leftClick = 1;
    if (e.buttons === leftClick) {
      setCursor('grabbing');
      containerRef.current.scrollTop -= e.movementY;
      containerRef.current.scrollLeft -= e.movementX;
    } else {
      setCursor('default');
    }
  }

  function handleMouseDown() {
    setCursor('grabbing');
  }

  function handleMouseUp() {
    setCursor('default');
  }

  function handleWheel(e) {
    if (e.ctrlKey || e.altKey) {
      if (e.deltaY > 0) {
        onScaleChange?.(scale - 0.1);
      } else {
        onScaleChange?.(scale + 0.1);
      }
    }
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      onMouseMove={handleMouseMove}
      style={{ cursor: cursor }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onWheel={handleWheel}
      onScroll={handleScroll}
      sx={{
        display: { display },
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        touchAction: 'pan-x pan-y',
      }}
    >
      <Pages
        pdf={pdf}
        scale={scale}
        pageNumber={pageNumber}
        onRenderSuccess={handleOnRenderSuccess}
        onLoadSuccess={handleOnLoadSuccess}
        onLoadError={onLoadError}
        highlights={highlights}
        citations={citations}
        setPageRef={handleSetPageRef}
        clearPageRef={handleClearPageRef}
        pageRotations={pageRotations}
        setCalculatedPageRotations={handleSetCalculatedPageRotations}
        showHighLight={showHighLight}
      />
    </Box>
  );
}

export default Area;
